if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span');

        const variantIdInput = this.variantIdInput;
        if (variantIdInput) {
          const selectionValid =
            variantIdInput.dataset.variantSelectionValid !== 'false' && Boolean(variantIdInput.value.trim());
          const purchaseEligible =
            selectionValid &&
            variantIdInput.dataset.purchaseEligible !== 'false' &&
            !variantIdInput.disabled &&
            !this.submitButton?.disabled;
          if (purchaseEligible) variantIdInput.disabled = false;
          else this.setPurchaseUnavailable(undefined, !selectionValid);
        }
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        if (document.querySelector('cart-drawer')) this.submitButton?.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (!this.submitButton || this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') {
          return;
        }

        const variantIdInput = this.variantIdInput;
        if (!variantIdInput || variantIdInput.disabled || !variantIdInput.value.trim()) {
          this.setPurchaseUnavailable(window.variantStrings?.unavailable, !variantIdInput?.value.trim());
          this.handleErrorMessage(window.variantStrings?.unavailable || 'Product variant unavailable');
          return;
        }

        // A previous cart rejection must not suppress the next successful cart
        // update after the customer selects a newly eligible variant.
        this.error = false;
        this.querySelector('[data-uv-cart-recovery]')?.remove();
        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.setPurchaseUnavailable(response.message || window.variantStrings?.soldOut);
              this.error = true;

              // A 422 may be a correctable quantity or recipient validation
              // error, not only a stock-out. Keep purchase fail-closed while
              // the section is revalidated, then let its server-rendered CTA
              // decide whether the same variant can be retried.
              await this.closest('product-info')?.fetchQuantityRules?.({ preserveError: true });

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage && this.submitButton.disabled) {
                this.submitButton.setAttribute('aria-disabled', true);
                this.submitButtonText?.classList.add('hidden');
                soldOutMessage.classList.remove('hidden');
              }
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error) {
              await publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            }
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.error = true;
            this.handleErrorMessage('No hemos podido confirmar la actualización. Comprueba tu carrito antes de volver a intentarlo.');
            // A lost response does not prove that Shopify rejected the addition.
            // Offer the real cart; never retry a purchase mutation automatically.
            this.querySelector('[data-uv-cart-recovery]')?.remove();
            const wrapper = this.querySelector('.product-form__error-message-wrapper');
            if (wrapper) {
              const link = document.createElement('a');
              link.href = window.routes.cart_url;
              link.className = 'button button--secondary uv-product__retry';
              link.dataset.uvCartRecovery = '';
              link.textContent = 'Comprobar mi carrito';
              wrapper.append(link);
            }
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (!this.error && this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.submitButton.disabled) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButton.removeAttribute('aria-disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      setPurchaseUnavailable(text, invalidateSelection = false) {
        const variantIdInput = this.variantIdInput;
        if (variantIdInput) {
          variantIdInput.disabled = true;
          variantIdInput.dataset.purchaseEligible = 'false';
          if (invalidateSelection) variantIdInput.dataset.variantSelectionValid = 'false';
        }

        this.toggleSubmitButton(true, text);
        const dynamicCheckout = this.querySelector('[id^="DynamicCheckout-"]');
        dynamicCheckout?.classList.add('hidden');
        dynamicCheckout?.setAttribute('aria-hidden', 'true');
      }

      get variantIdInput() {
        return this.form?.querySelector('.product-variant-id[name="id"]') || null;
      }
    }
  );
}
